//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState } from 'react';
import { useTheme } from '@material-ui/core';
import { VerifiedUserRounded, Message, AccessTime, Business, School } from '@material-ui/icons';
import { makeStyles, Grid, Badge, Link, useMediaQuery, Tooltip } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { mdiSchoolOutline } from '@mdi/js';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import cx from 'clsx';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _defaultTo from 'lodash/defaultTo';
import _omit from 'lodash/omit';
import _forEach from 'lodash/forEach';
import _keys from 'lodash/keys';

import { format, getTime, differenceInSeconds } from 'date-fns';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { logEvent } from '../../system/firebase';
import { AppContext, LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';
import {
    DASHBOARD_MY_MESSAGES,
    CONFIRM_DIALOG_TITLE_APPLY_JOB,
    CONFIRM_DIALOG_MESSAGE_APPLY_JOB,
    MESSAGE_ONLY_JS_CAN_APPLY_JOB,
    MESSAGE_ONLY_JS_CAN_SAVE_JOB,
    VISIT_BRAND_PAGE,
    CONFIRM_DIALOG_TITLE_MAKE_PROFILE_PUBLIC,
    CONFIRM_DIALOG_MESSAGE_MAKE_PROFILE_PUBLIC,
    BASE_URL,
    DASHBOARD_ID_MY_PROFILE,
    UPDATED_ON,
    POSTED_ON,
    ANALYTICS_EVENT_JS_APPLIED_JOB,
    ANALYTICS_EVENT_JS_SAVED_JOB,
    MESSAGE_TITLE_ONLY_JS_CAN_APPLY_JOB,
} from '../../common/constants';
import { PATH_DASHBOARD, PATH_SIGN_IN } from '../../system/constants';
import { getCityStateCountryText, getFullNameByUser, getJobYearsOfExperienceText, getSalaryRateText } from '../../common/textHelper';
import { getUniqueKey } from '../../common/arrayHelper';
import { getBrandPageUrlPathByEmployerId } from '../../common/linkHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { applyJob, bookmarkJob } from '../../common/services/jobCenterServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdMdiIcon from '../../common/elemental/AfitdMdiIcon';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdConfirmDialog from '../../common/elemental/AfitdConfirmDialog';
import AfitdAvatar from '../../common/elemental/AfitdAvatar';
import AfitdLink from '../../common/elemental/AfitdLink';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import Tabs from './Tabs';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { isBrandPage } = params;

    return makeStyles(theme => ({
        jobDetailContainer: {
            overflowY: 'hidden',
            display: 'flex',
            padding: '30px',
            paddingBottom: 0,
            flexDirection: 'row',
            backgroundColor: '#fff',
            marginLeft: '20px',
            marginTop: isBrandPage ? 0 : 10, // on Brand Page, align with SideBar
            borderRadius: '10px',
            height: '100%',
            // minHeight: '690px',
        },
        logoLinkContainer: {
            cursor: 'pointer',
        },
        companyProfilePic: {
            width: 100,
            height: 100,
            marginRight: '24px',
            border: '1px solid #D5D7DD',
            background: 'rgba(252, 130, 31, 0.1)',
            color: 'rgb(252,130,31)',
            fontSize: '35px',
        },
        jobContentContainer: {
            height: '100%',
            overflow: 'hidden',
            paddingRight: '8px', // temp fix
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
        },
        headingSection: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            paddingBottom: 10,
            [theme.breakpoints.down('sm')]: { flexDirection: 'column-reverse', alignItems: 'flex-start' },
        },
        jobTitleText: {
            textTransform: 'capitalize',
            fontWeight: '700',
            display: 'block',
            fontSize: '20px',
            color: '#111e14',
            flex: 1,
        },
        publishedOn: {
            color: '#1D8A8B',
            textTransform: 'uppercase',
            fontWeight: '600',
            fontSize: '13px',
            marginLeft: '12px',
            lineHeight: '12.89px',
            [theme.breakpoints.down('sm')]: { marginLeft: '0px', paddingBottom: 8 },
        },
        verifyIcon: {
            color: '#1D8A8B',
            margin: '0 5px',
        },
        locationContainer: {
            padding: '0 0 10 0',
            display: 'flex',
        },
        metaMessageBadgeContainer: {
            // display: 'flex',
            overflowY: 'auto',
            overflowX: 'hidden',
            '&::-webkit-scrollbar': {
                width: '0.4em',
            },
            '&::-webkit-scrollbar-track': {
                '-webkit-box-shadow': 'inset 0 0 6px rgba(0,0,0,0.00)',
            },
            '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,.1)',
                borderRadius: '5px',
            },
        },
        detailContainer: {
            flexDirection: 'row',
            display: 'flex',
            alignItems: 'center',
            paddingRight: '10px',
        },
        companyNameLink: {
            textDecoration: 'underline',
        },
        companyNameText: {
            letterSpacing: '0em',
            color: '#1D8A8B',
            textDecoration: 'underline',
            cursor: 'pointer',
            display: 'inline-block',
        },
        jobLocationText: { marginBottom: 10 },
        appliedOn: {
            fontWeight: '500',
            letterSpacing: '0em',
            color: '#1D8A8B',
        },
        detail: {
            color: '#111e14',
            // paddingLeft: '6px',
            marginRight: '6px',
            fontWeight: 'bold',
            margin: '1px',
            letterSpacing: '0em',
            whiteSpace: 'nowrap',
        },
        // icons: {
        //     padding: 0,
        //     fontSize: '20px',
        //     cursor: 'pointer',
        //     color: '#9B9E9C',
        // },
        // detail: { fontWeight: 'bold' },
        icons: { color: '#9B9E9C', fontWeight: 500, fontSize: 20, marginRight: 10 },
        currencySymbol: { color: '#9B9E9C', fontSize: 20, fontWeight: 500, marginRight: 10 },

        tabsContainer: {
            flex: 1,
            // overflow: 'hidden',
            paddingTop: '35px',
        },
        // '@media (max-width: 912px)': {
        //     jobDetailContainer: {
        //         flexDirection: 'column',
        //     },
        // },
        '@media (max-width: 800px)': {
            jobDetailContainer: {
                // flexDirection: 'column',
                // padding: 0,
                margin: 0,
            },
        },
        '@media (max-width: 763px)': {
            jobDetailContainer: {
                padding: '24px 16px',
            },
        },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        props,
        // appContext,
        layoutContext,
        appliedJobs,
        handleClickApplyJobButton,
        confirmApplyJobDialogOpen,
        setConfirmApplyJobDialogOpen,
        handleClickConfirmApplyJobDialogOkButton,
        handleClickSaveJobButton,
        handleClickApplyOnCompanySite,
        employerAttemptApplyJobDialogOpen,
        setEmployerAttemptApplyJobDialogOpen,
        employerAttemptSaveJobDialogOpen,
        setEmployerAttemptSaveJobDialogOpen,
        history,
        conversationExists,
        handleClickCompanyLogo,
        // setBrandPageDialogInfo,
        mdMatchesUp,
        makeProfilePublicDialogOpen,
        setMakeProfilePublicJobDialogOpen,
        handleClickConfirmMakeProfilePublicDialogOkButton,
        handleClickEmployerAttemptJsActionsDialogDialogCreateAccountButton,
    } = params;

    const { detail: detailCls } = classes;

    const { job = {} } = props;

    // const { currentUser } = appContext;
    const { isBrandPage /*, setSnackbarMessage, setSnackbarErrorMessage */ } = layoutContext;

    const {
        // intern,
        jobTitle,
        yrsOfExp,
        isExactRate,
        rateMin,
        rateMax,
        telework,
        education,
        employmentType,
        // uid,
        companyProfilePicUrl,
        companyName,
        // applicants,
        // activeDt,
        // createDt,
        // conversations,
        currency,
        currencySymbol,
        payFrequency,
        country,
        state,
        city,
        employerId: jobEmployerId,
        // isEmpBadge,
        // rate,
        // jobApplicationUrl,
    } = job;
    // const companyProfilePicUrl = _get(job, 'companyProfilePicUrl');

    const jobLocationText = getCityStateCountryText(city, state, country);

    const educationText = education;

    const defns = {
        employerLogoLink: {
            isBrandPage,
            onClick: handleClickCompanyLogo,
            profilePicUrl: companyProfilePicUrl,
            brandEmployerId: jobEmployerId,
        },
        jobTitleText: {
            value: jobTitle,
        },
        publishedOn: {
            variant: 'body1',
        },
        companyNameLink: {
            color: 'secondary',
            title: "Visit Company's Brand Page",
            onClick: mdMatchesUp ? undefined : handleClickCompanyLogo,
            href: mdMatchesUp ? getBrandPageUrlPathByEmployerId(jobEmployerId) : undefined,
            target: mdMatchesUp ? '_blank' : undefined,
        },
        companyNameText: {
            variant: 'body1',
            color: 'secondary',
            value: companyName,
            isBrandPage,
            brandEmployerId: jobEmployerId,
        },
        jobLocationText: { variant: 'body1', value: jobLocationText },
        // detail: {
        //     variant: 'body1',
        // },
        currencySymbol: {
            value: currencySymbol,
        },
        currencyText: {
            variant: 'body1',
            className: cx(detailCls),
            value: getSalaryRateText(isExactRate, rateMin, rateMax, currency, payFrequency),
        },
        yrsOfExpText: {
            variant: 'body1',
            className: cx(detailCls),
            value: getJobYearsOfExperienceText(yrsOfExp),
        },
        employmentTypeText: {
            variant: 'body1',
            className: cx(detailCls),
            value: employmentType,
        },
        teleworkText: { variant: 'body1', className: cx(detailCls), value: telework },
        internshipIcon: {
            // color: 'watermark',
            style: { color: '#9B9E9C', width: 22, marginRight: 10 },
            path: mdiSchoolOutline,
        },
        internshipText: { variant: 'body1', className: cx(detailCls), value: 'Intern Position' },
        educationText: { variant: 'body1', className: cx(detailCls), value: educationText },
        otherEducationText: {
            variant: 'body1',
            className: cx(detailCls),
        },

        messageBadge: {
            color: 'secondary',
            variant: 'dot',
        },
        metaContainer: {
            container: true,
            item: true,
            spacing: 1,
        },
        messageIconContainer: {
            onClick: () =>
                history.push('/', {
                    pathname: '/',
                    state: { currentDashboard: DASHBOARD_MY_MESSAGES, threadId: conversationExists?.threadId },
                }),
        },
        tabs: {
            ...job,
            appliedJobs,
            applyOnCompanySiteOnClick: handleClickApplyOnCompanySite,
            applyJobOnClick: handleClickApplyJobButton,
            saveJobOnClick: handleClickSaveJobButton,
        },
        confirmModal: {
            title: CONFIRM_DIALOG_TITLE_APPLY_JOB,
            onClose: () => setConfirmApplyJobDialogOpen(false),
            onConfirm: handleClickConfirmApplyJobDialogOkButton,
            open: confirmApplyJobDialogOpen,
            message: CONFIRM_DIALOG_MESSAGE_APPLY_JOB,
        },
        makeProfilePublic: {
            title: CONFIRM_DIALOG_TITLE_MAKE_PROFILE_PUBLIC,
            onClose: () => setMakeProfilePublicJobDialogOpen(false),
            onConfirm: handleClickConfirmMakeProfilePublicDialogOkButton,
            open: makeProfilePublicDialogOpen,
            message: CONFIRM_DIALOG_MESSAGE_MAKE_PROFILE_PUBLIC,
            yesButtonLabel: 'Update My Profile',
            noButtonLabel: 'Cancel',
            yesButtonColor: 'primary',
        },
        employerAttemptJsActionsDialog: {
            title: MESSAGE_TITLE_ONLY_JS_CAN_APPLY_JOB,
            message: employerAttemptApplyJobDialogOpen ? MESSAGE_ONLY_JS_CAN_APPLY_JOB : employerAttemptSaveJobDialogOpen ? MESSAGE_ONLY_JS_CAN_SAVE_JOB : null,
            open: employerAttemptSaveJobDialogOpen || employerAttemptApplyJobDialogOpen,
            noButtonLabel: 'Close',
            yesButtonLabel: 'Create an Account',
            yesButtonColor: 'primary',
            onConfirm: handleClickEmployerAttemptJsActionsDialogDialogCreateAccountButton,
            onClose: () => {
                setEmployerAttemptApplyJobDialogOpen(false);
                setEmployerAttemptSaveJobDialogOpen(false);
            },
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const EmployerLogoLink = props => {
    const { isBrandPage, brandEmployerId, onClick, profilePicUrl } = props;
    const classes = useStyles({ isBrandPage })();
    const theme = useTheme();
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    const defns = {
        logoLinkContainer: {
            onClick: isBrandPage ? undefined : onClick,
        },
        logoLink: {
            href: getBrandPageUrlPathByEmployerId(brandEmployerId),
            target: '_blank',
        },
        companyProfilePic: {
            variant: 'rounded',
            src: profilePicUrl,
        },
        tooltip: {
            title: VISIT_BRAND_PAGE,
        },
    };

    // inject classes
    setClassNameInDefns(defns, classes);

    return isBrandPage ? (
        <div {...defns.logoContainer}>
            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
        </div>
    ) : (
        <div {...defns.logoContainer}>
            <Tooltip {...defns.tooltip}>
                <div>
                    {mdMatchesUp ? (
                        <Link {...defns.logoLink}>
                            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
                        </Link>
                    ) : smMatches ? null : (
                        <div {...defns.logoLinkContainer}>
                            <AfitdAvatar {...defns.companyProfilePic}>?</AfitdAvatar>
                        </div>
                    )}
                </div>
            </Tooltip>
        </div>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const JobDetail = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { authUser, userId, currentUser, employerId } = appContext;

    const layoutContext = useContext(LayoutContext);
    const { isBrandPage, setBrandPageDialogInfo, setTempBookmarkedJobs, setTempUnbookmarkedJobs, setTempAppliedJobs, setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;
    const history = useHistory();

    const theme = useTheme();
    const mdMatchesUp = useMediaQuery(theme.breakpoints.up('md'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { job = {} } = props;
    const job = _defaultTo(_get(props, 'job'), {});

    console.log('JobDetail', JSON.stringify({ job }));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [confirmApplyJobDialogOpen, setConfirmApplyJobDialogOpen] = useState(false);
    const [makeProfilePublicDialogOpen, setMakeProfilePublicJobDialogOpen] = useState(false);
    const [employerAttemptApplyJobDialogOpen, setEmployerAttemptApplyJobDialogOpen] = useState(false);
    const [employerAttemptSaveJobDialogOpen, setEmployerAttemptSaveJobDialogOpen] = useState(false);

    //---------------------------------------------------------------------
    // derived
    //---------------------------------------------------------------------
    const isEmployer = !!employerId;
    const {
        intern,
        // jobTitle,
        // salary,
        // otherSalary,
        // isExactRate,
        // rateMin,
        // rateMax,
        // telework,
        // education,
        // employmentType,
        // uid,
        // companyProfilePicUrl, //profileImage,
        // companyName,
        // applicants,
        activeDt,
        // createDt,
        conversations,
        // currency,
        // currencySymbol,
        // payFrequency,
        // otherEducation,
        employerId: jobEmployerId,
        isEmpBadge,
        // rate,
        jobApplicationUrl,
        updateDt,
        uid: jobId,
        otherEducation,
        education,
    } = job;

    // const applied = (applicants || []).find(el => el.userId === userId);
    const appliedJobs = _get(currentUser, 'appliedJobs');
    const appliedDt = _get(appliedJobs, jobId);
    const applied = !!appliedDt;
    const isShowAppliedText = applied && !isBrandPage;
    const conversationExists = (conversations || []).find(el => el.members.includes(userId));
    const isBookmarked = currentUser?.bookmarkedJobs?.includes(jobId);

    const updatedAfterActive = getTime(updateDt) > getTime(activeDt);
    const displayDt = updatedAfterActive ? updateDt : activeDt;
    const displayDtLabel = updatedAfterActive ? UPDATED_ON : POSTED_ON;

    const jsName = getFullNameByUser(currentUser);
    const jsCountry = _get(currentUser, 'country');

    //---------------------------------------------------------------------
    // Event handlers
    //---------------------------------------------------------------------
    const handleClickConfirmApplyJobDialogOkButton = async evt => {
        const employerId = _get(job, 'employerId');
        const success = await applyJob({
            userId,
            jsName,
            jsCountry,
            employerId,
            jobId,
            callback: () => {
                logEvent(ANALYTICS_EVENT_JS_APPLIED_JOB);
            },
            setSnackbarMessage,
            setSnackbarErrorMessage,
        });

        if (success) {
            // remember the job for illusion of real-time
            setTempAppliedJobs(prev => {
                let cleanedUpList = { ...prev };
                _forEach(_keys(prev), jobId => {
                    if (differenceInSeconds(new Date(), prev[jobId].applyDt) > 30) cleanedUpList = _omit(cleanedUpList, jobId);
                });
                return { ...cleanedUpList, [job.uid]: { data: job, applyDt: new Date() } };
            });
        }
    };

    const handleClickConfirmMakeProfilePublicDialogOkButton = evt => {
        history.push(`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_PROFILE}`);
        setMakeProfilePublicJobDialogOpen(false);
    };

    const handleClickEmployerAttemptJsActionsDialogDialogCreateAccountButton = evt => {
        history.push(`${BASE_URL}${PATH_SIGN_IN}/j`);
        setEmployerAttemptSaveJobDialogOpen(false);
    };

    const handleClickApplyJobButton = () => {
        if (!authUser || isEmployer) setEmployerAttemptApplyJobDialogOpen(true);
        else {
            if (currentUser?.publicProfile) setConfirmApplyJobDialogOpen(true);
            else setMakeProfilePublicJobDialogOpen(true);
        }
    };

    const handleClickSaveJobButton = async () => {
        if (!authUser || isEmployer) {
            setEmployerAttemptSaveJobDialogOpen(true);
        } else {
            const isAdd = !isBookmarked;
            const success = await bookmarkJob({ isAdd, userId, jobId, setSnackbarMessage, setSnackbarErrorMessage });
            success && logEvent(ANALYTICS_EVENT_JS_SAVED_JOB);

            if (success) {
                if (isAdd) {
                    // remember the job for illusion of real-time
                    setTempBookmarkedJobs(prev => {
                        let cleanedUpList = { ...prev };
                        _forEach(_keys(prev), jobId => {
                            if (differenceInSeconds(new Date(), prev[jobId].updateDt) > 30) cleanedUpList = _omit(cleanedUpList, jobId);
                        });
                        return { ...cleanedUpList, [job.uid]: { data: job, updateDt: new Date() } };
                    });
                    // clean up the other list
                    setTempUnbookmarkedJobs(prev => _omit({ ...prev }, [job.uid]));
                } else {
                    // remember the job Id for illusion of real-time
                    setTempUnbookmarkedJobs(prev => {
                        let cleanedUpList = { ...prev };
                        _forEach(_keys(prev), jobId => {
                            if (differenceInSeconds(new Date(), prev[jobId].deleteDt) > 30) cleanedUpList = _omit(cleanedUpList, jobId);
                        });
                        return { ...cleanedUpList, [jobId]: { id: jobId, deleteDt: new Date() } };
                    });
                    // clean up the other list
                    setTempBookmarkedJobs(prev => _omit({ ...prev }, [job.uid]));
                }
            }
        }
    };

    const handleClickApplyOnCompanySite = () => {
        window.open(jobApplicationUrl, '_blank');
    };

    const handleClickCompanyLogo = evt => {
        evt.stopPropagation();
        setBrandPageDialogInfo({ open: true, employerId: jobEmployerId });
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const classes = useStyles({ isBrandPage })();
    if (_isEmpty(job)) return null;
    const defns = getDefns({
        classes,
        props,
        appContext,
        layoutContext,
        appliedJobs,
        handleClickApplyJobButton,
        confirmApplyJobDialogOpen,
        setConfirmApplyJobDialogOpen,
        handleClickConfirmApplyJobDialogOkButton,
        handleClickSaveJobButton,
        handleClickApplyOnCompanySite,
        employerAttemptApplyJobDialogOpen,
        setEmployerAttemptApplyJobDialogOpen,
        employerAttemptSaveJobDialogOpen,
        setEmployerAttemptSaveJobDialogOpen,
        history,
        conversationExists,
        handleClickCompanyLogo,
        setBrandPageDialogInfo,
        mdMatchesUp,
        makeProfilePublicDialogOpen,
        setMakeProfilePublicJobDialogOpen,
        handleClickConfirmMakeProfilePublicDialogOkButton,
        handleClickEmployerAttemptJsActionsDialogDialogCreateAccountButton,
    });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <div key={getUniqueKey()} {...defns.jobDetailContainer}>
            {smMatches ? null : <EmployerLogoLink {...defns.employerLogoLink} />}

            <div {...defns.jobContentContainer}>
                <div>
                    <div {...defns.headingSection}>
                        <AfitdTypography {...defns.jobTitleText} />
                        <div style={{ justifyContent: 'flex-end' }}>
                            {isShowAppliedText && <AfitdTypography {...defns.publishedOn}> APPLIED on {format(appliedDt, 'MM/dd/yyyy')}</AfitdTypography>}
                            {!isShowAppliedText && (
                                <AfitdTypography {...defns.publishedOn}>
                                    <i> {displayDtLabel} </i>
                                    {format(new Date(displayDt), 'MM/dd/yyyy')}
                                </AfitdTypography>
                            )}
                        </div>
                    </div>
                    <div {...defns.locationContainer}>
                        <AfitdLink {...defns.companyNameLink}>
                            <AfitdTypography {...defns.companyNameText} />
                        </AfitdLink>
                        {isEmpBadge && <VerifiedUserRounded {...defns.verifyIcon} />}
                    </div>
                    <AfitdTypography {...defns.jobLocationText} />
                </div>
                <div {...defns.metaMessageBadgeContainer}>
                    <Grid {...defns.metaContainer}>
                        <Grid item>
                            <div {...defns.detailContainer}>
                                <AfitdTypography {...defns.currencySymbol} />
                                <AfitdTypography {...defns.currencyText} />
                            </div>
                        </Grid>
                        <Grid item>
                            <div {...defns.detailContainer}>
                                <AccessTime {...defns.icons} />
                                <AfitdTypography {...defns.detail} {...defns.yrsOfExpText} />
                            </div>
                        </Grid>
                        <Grid item>
                            <div {...defns.detailContainer}>
                                <AccessTime {...defns.icons} />
                                <AfitdTypography {...defns.detail} {...defns.employmentTypeText} />
                            </div>
                        </Grid>
                        <Grid item>
                            <div {...defns.detailContainer}>
                                <Business {...defns.icons} />
                                <AfitdTypography {...defns.detail} {...defns.teleworkText} />
                            </div>
                        </Grid>
                        {intern ? (
                            <Grid item>
                                <div {...defns.detailContainer}>
                                    <AfitdMdiIcon {...defns.internshipIcon} />
                                    <AfitdTypography {...defns.detail} {...defns.internshipText} />
                                </div>
                            </Grid>
                        ) : null}
                        {education && (
                            <Grid item>
                                <div {...defns.detailContainer}>
                                    <School {...defns.icons} />
                                    <AfitdTypography {...defns.detail} {...defns.educationText} />
                                </div>
                            </Grid>
                        )}
                        {otherEducation?.map(el => (
                            <Grid item>
                                <div {...defns.detailContainer}>
                                    <School {...defns.icons} />
                                    <AfitdTypography {...defns.detail} {...defns.otherEducationText}>
                                        {el}
                                    </AfitdTypography>
                                </div>
                            </Grid>
                        ))}
                    </Grid>
                    {/* <div>
                        <Badge {...defns.messageBadge}>
                            <Message {...defns.icons} />
                        </Badge>
                    </div> */}
                    {conversationExists && (
                        <div {...defns.messageIconContainer}>
                            {conversationExists.unread ? (
                                <Badge {...defns.messageBadge}>
                                    <Message {...defns.icons} />
                                </Badge>
                            ) : (
                                <Message {...defns.icons} />
                            )}
                        </div>
                    )}

                    <div {...defns.tabsContainer}>
                        <Tabs {...defns.tabs} />
                    </div>
                </div>
            </div>
            {/* Confirm apply modal */}
            <AfitdConfirmDialog {...defns.confirmModal} />
            {/* Make profile public Modal */}
            <AfitdConfirmDialog {...defns.makeProfilePublic} />
            <AfitdConfirmDialog {...defns.employerAttemptJsActionsDialog} />
        </div>
    );
};

export default JobDetail;
